import PropTypes from 'prop-types';
import React from 'react';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import {
  ActionIcon,
  Icon,
  PageShell,
  tokens,
  useIsMobile,
} from 'components/v3';
import { FormBuilder } from '../Forms/FormBuilder';
import useFormsToaster from '../Forms/shared/useFormsToaster';
import Glyph from '../Forms/Submissions/Glyph';
import { Card, SectionBody, SectionHeader } from './components/SectionShell';
import { w9SettingsPath } from './shared/routes';
import useW9Accounts from './shared/useW9Accounts';
import useW9Section from './shared/useW9Section';
import { W9_SYSTEM_IDS } from './shared/w9Template';

// Settings → W-9s → Settings (w9-plan §6.5; design w9_app.jsx
// SettingsPage): the real Form Builder on the account's W-9 form with the
// IRS fields locked, so only the payee slot (step one) and Notifications
// can change. Save, the conflict reload, Preview and the Notifications-only
// settings panel come from the builder; this page adds the W-9 header in
// place of the Forms one (no Live/Draft, no Build | Submissions) and the
// Security & retention card below.

export const PAYEE_BLURB =
  'A W-9 alone doesn’t tell you who someone is at your track. Ask a few quick questions on step one, like their division or car number, so every W-9 comes in tied to a driver you know. Answers show as columns and filters on the dashboard. The IRS fields are locked.';

export const SECURITY_FACTS = [
  {
    icon: 'shield-check',
    text: 'TINs are encrypted at rest and masked by default. Every full reveal is written to the access log.',
  },
  {
    icon: 'sheet',
    text: 'CSV, Excel and PDF exports never include full TINs or signatures.',
  },
  {
    icon: 'archive',
    text: 'W-9s are kept for at least four years after their tax year, per IRS record-keeping guidance. Archived years are read-only.',
  },
];

// The builder's header slot (FormBuilder renderHeader): the W-9 title,
// Preview, and the settings (Notifications) button where the builder
// asks for one (narrow desktop and phones).
export const SettingsHead = ({
  accountName,
  dirty,
  onPreview,
  onOpenSettings,
  showSettingsButton,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const isMobile = useIsMobile();
  return (
    <header
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px 16px',
        padding: isMobile ? '14px 16px' : '14px 16px 14px 20px',
        marginBottom: 16,
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
      }}
    >
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <h2
          style={{
            margin: 0,
            fontSize: isMobile ? 18 : 20,
            fontWeight: 800,
            letterSpacing: '-0.3px',
            color: t.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          W-9 Settings · {accountName}
        </h2>
        <div
          style={{ marginTop: 4, fontSize: 13, lineHeight: 1.5, color: t.sub }}
        >
          {PAYEE_BLURB}
          {dirty ? ' Unsaved changes.' : ''}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginLeft: isMobile ? 0 : 'auto',
          width: isMobile ? '100%' : 'auto',
        }}
      >
        {onPreview ? (
          <ButtonV2
            onClick={onPreview}
            icon={<Icon name="smartphone" size={16} color="currentColor" />}
            style={{ width: isMobile ? '100%' : 'auto', whiteSpace: 'nowrap' }}
          >
            Preview
          </ButtonV2>
        ) : null}
        {onOpenSettings && (showSettingsButton || isMobile) ? (
          <ActionIcon
            icon="settings"
            title="Notifications"
            onClick={onOpenSettings}
          />
        ) : null}
      </div>
    </header>
  );
};

SettingsHead.propTypes = {
  accountName: PropTypes.string.isRequired,
  dirty: PropTypes.bool,
  onPreview: PropTypes.func,
  onOpenSettings: PropTypes.func,
  showSettingsButton: PropTypes.bool,
};

SettingsHead.defaultProps = {
  dirty: false,
  onPreview: undefined,
  onOpenSettings: undefined,
  showSettingsButton: false,
};

export const SecurityCard = () => {
  const t = tokens(useTheme());
  return (
    <Card title="Security & retention" style={{ marginTop: 16, maxWidth: 760 }}>
      <div>
        {SECURITY_FACTS.map((fact, i) => (
          <div
            key={fact.icon}
            style={{
              display: 'flex',
              gap: 12,
              padding: '12px 16px',
              borderTop: i ? '1px solid ' + t.line : 'none',
              fontSize: 13,
              lineHeight: 1.5,
              color: t.sub,
            }}
          >
            <span style={{ flex: 'none', marginTop: 1, color: t.faint }}>
              <Glyph name={fact.icon} size={16} />
            </span>
            <span>{fact.text}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

const SettingsPage = () => {
  const section = useW9Section(w9SettingsPath);
  const { accounts, loading: accountsLoading } = useW9Accounts();
  const { toasterNode } = useFormsToaster();
  return (
    <PageShell>
      <SectionHeader
        page="settings"
        routePrefix={section.routePrefix}
        accountId={section.accountId}
        accounts={accounts}
        accountsLoading={accountsLoading}
        onPickAccount={section.pickAccount}
      />
      <SectionBody
        accountId={section.accountId}
        form={section.form}
        loading={section.loading}
        error={section.error}
        onRetry={section.retry}
      >
        {(form) => (
          <div key={form.id} data-w9-settings="">
            <FormBuilder
              formId={form.id}
              lockedFieldIds={W9_SYSTEM_IDS}
              w9Redirect={false}
              renderHeader={(headerProps) => (
                <SettingsHead
                  accountName={
                    (headerProps.form &&
                      headerProps.form.account &&
                      headerProps.form.account.name) ||
                    form.account.name
                  }
                  dirty={!!headerProps.dirty}
                  onPreview={headerProps.onPreview}
                  onOpenSettings={headerProps.onOpenSettings}
                  showSettingsButton={!!headerProps.showSettingsButton}
                />
              )}
            />
            <SecurityCard />
          </div>
        )}
      </SectionBody>
      {toasterNode}
    </PageShell>
  );
};

export default SettingsPage;
